'use client';

import { c as _c } from "react/compiler-runtime";
import { NavArrowLeft, NavArrowRight } from '@byway/icons';
import { type CalendarDate, DateFormatter, getLocalTimeZone, today } from '@internationalized/date';
import { clsx } from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { RangeCalendar as AriaRangeCalendar, CalendarCell, CalendarGrid, CalendarGridBody, CalendarGridHeader, CalendarHeaderCell, type DateValue, Heading, Text } from 'react-aria-components';
import { Button } from '~/components/Button';
import styles from './RangeCalendar.module.css';
import type { RangeCalendarProps } from './RangeCalendar.types';

/**
 * React Aria's RangeCalendar only fires onChange after two clicks. AutoComplete bridges
 * that gap for fixed-duration trips: when anchorDate transitions null → CalendarDate
 * (the user's first click), it immediately calls state.selectDate(anchor + fixedDuration - 1),
 * completing the range in a single click.
 *
 * useEffect is appropriate here because we're synchronising with React Aria's internal
 * state (an external system) — not React-state-to-React-state transformation.
 */
interface SelectableState {
  anchorDate: CalendarDate | null;
  setValue: (value: {
    start: CalendarDate;
    end: CalendarDate;
  }) => void;
  setAnchorDate: (date: CalendarDate | null) => void;
}
const AutoComplete = ({
  anchorDate,
  fixedDuration,
  state
}: {
  anchorDate: CalendarDate | null;
  fixedDuration: number;
  state: SelectableState;
}) => {
  const prevAnchor = useRef<CalendarDate | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => {
    if (anchorDate && !prevAnchor.current) {
      stateRef.current.setValue({
        start: anchorDate,
        end: anchorDate.add({
          days: fixedDuration - 1
        })
      });
      stateRef.current.setAnchorDate(null);
    }
    prevAnchor.current = anchorDate;
  }, [anchorDate, fixedDuration]);
  return null;
};
const ChoosingEndSync = t0 => {
  const $ = _c(4);
  const {
    isChoosingEnd,
    onChange
  } = t0;
  let t1;
  let t2;
  if ($[0] !== isChoosingEnd || $[1] !== onChange) {
    t1 = () => {
      onChange(isChoosingEnd);
    };
    t2 = [isChoosingEnd, onChange];
    $[0] = isChoosingEnd;
    $[1] = onChange;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
};
const RangeCalendar = t0 => {
  const $ = _c(40);
  let errorMessage;
  let fixedDuration;
  let isStartDateUnavailable;
  let minDuration;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      errorMessage,
      minDuration,
      fixedDuration,
      isStartDateUnavailable,
      layout: t1,
      monthsToShow: t2,
      variant: t3,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = errorMessage;
    $[2] = fixedDuration;
    $[3] = isStartDateUnavailable;
    $[4] = minDuration;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    errorMessage = $[1];
    fixedDuration = $[2];
    isStartDateUnavailable = $[3];
    minDuration = $[4];
    props = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
  }
  const layout = t1 === undefined ? "vertical" : t1;
  const monthsToShow = t2 === undefined ? 2 : t2;
  const variant = t3 === undefined ? "default" : t3;
  const t = useTranslations("aria");
  const locale = useLocale();
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    const now = today(getLocalTimeZone());
    t4 = now.add({
      days: 14
    });
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const earliestBookingDate = t4;
  const isHorizontal = layout === "horizontal";
  const isConnected = variant === "connected";
  const [isChoosingEnd, setIsChoosingEnd] = useState(false);
  const isDateUnavailable = _temp;
  const selectedRange = props.value;
  let t5;
  if ($[10] !== isStartDateUnavailable || $[11] !== selectedRange) {
    t5 = date_0 => !!isStartDateUnavailable?.(date_0) && !(selectedRange && date_0.compare(selectedRange.start) >= 0 && date_0.compare(selectedRange.end) <= 0);
    $[10] = isStartDateUnavailable;
    $[11] = selectedRange;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const isBlockedStartDate = t5;
  let t6;
  if ($[13] !== isBlockedStartDate || $[14] !== isChoosingEnd || $[15] !== props) {
    t6 = date_1 => !!props.isDateUnavailable?.(date_1) || !isChoosingEnd && isBlockedStartDate(date_1);
    $[13] = isBlockedStartDate;
    $[14] = isChoosingEnd;
    $[15] = props;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const isCellUnavailable = t6;
  let t7;
  if ($[17] !== locale) {
    t7 = new DateFormatter(locale, {
      month: "long",
      year: "numeric"
    });
    $[17] = locale;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const monthFormatter = t7;
  let t8;
  if ($[19] !== monthsToShow || $[20] !== props.visibleDuration) {
    t8 = props.visibleDuration ?? {
      months: monthsToShow
    };
    $[19] = monthsToShow;
    $[20] = props.visibleDuration;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = isConnected && styles.connected;
  let t10;
  if ($[22] !== t9) {
    t10 = clsx(styles["react-aria-RangeCalendar"], t9);
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== errorMessage || $[25] !== fixedDuration || $[26] !== isConnected || $[27] !== isHorizontal || $[28] !== isStartDateUnavailable || $[29] !== minDuration || $[30] !== monthFormatter || $[31] !== monthsToShow || $[32] !== t) {
    t11 = renderProps => {
      const visibleRange = renderProps.state.visibleRange;
      const firstMonth = visibleRange.start;
      const secondMonth = firstMonth.add({
        months: 1
      });
      return <>{isStartDateUnavailable && <ChoosingEndSync isChoosingEnd={renderProps.state.anchorDate != null} onChange={setIsChoosingEnd} />}{fixedDuration != null && <AutoComplete anchorDate={renderProps.state.anchorDate} fixedDuration={fixedDuration} state={renderProps.state as SelectableState} />}{isHorizontal ? <Heading className={styles.hiddenHeading} /> : <header className={styles.header}><Button variant="icon" ariaLabel={t("prev_month")} slot="previous"><NavArrowLeft decorative={true} size="large" color="currentColor" /></Button><Heading className={styles.hiddenHeading} /><Button variant="icon" ariaLabel={t("next_month")} slot="next"><NavArrowRight decorative={true} size="large" color="currentColor" /></Button></header>}<div className={clsx(styles.monthsContainer, {
          [styles.monthsContainerHorizontal]: isHorizontal
        })}><div className={clsx(styles.monthContainer, isHorizontal && styles.monthContainerHorizontal)}>{isHorizontal ? <div className={styles.horizontalMonthHeader}><Button variant="icon" ariaLabel={t("prev_month")} slot="previous"><NavArrowLeft decorative={true} size="large" color="currentColor" /></Button><h3 className={clsx(styles.monthHeader, styles.monthHeaderHorizontal)} data-test-id="range-calendar-month-header-1">{monthFormatter.format(firstMonth.toDate(getLocalTimeZone()))}</h3></div> : <h3 className={styles.monthHeader} data-test-id="range-calendar-month-header-1">{monthFormatter.format(firstMonth.toDate(getLocalTimeZone()))}</h3>}<CalendarGrid weekdayStyle={isConnected ? "narrow" : "short"} className={styles["react-aria-CalendarGrid"]}><CalendarGridHeader>{_temp2}</CalendarGridHeader><CalendarGridBody className={styles["react-aria-CalendarGridBody"]}>{date_2 => {
                  const isAnchorDate = renderProps.state.anchorDate ? date_2.compare(renderProps.state.anchorDate) === 0 : false;
                  const withinMinDuration = isDateUnavailable(date_2, minDuration, renderProps.state.anchorDate);
                  const dow = (date_2.toDate(getLocalTimeZone()).getDay() + 6) % 7;
                  return <CalendarCell date={date_2} className={t12 => {
                    const {
                      isUnavailable: isUnavailable_0
                    } = t12;
                    return clsx({
                      [styles["react-aria-CalendarCell"]]: true,
                      [styles.isRoundedLeft]: dow === 0 || date_2.day === 1,
                      [styles.isRoundedRight]: dow === 6 || date_2.day === date_2.calendar.getDaysInMonth(date_2),
                      [styles.isUnavailable]: isUnavailable_0 || withinMinDuration,
                      [styles.isAnchorDate]: isAnchorDate
                    });
                  }}>{isConnected ? _temp3 : undefined}</CalendarCell>;
                }}</CalendarGridBody></CalendarGrid></div>{monthsToShow === 2 && <div className={clsx(styles.monthContainer, isHorizontal && styles.monthContainerHorizontal)}>{isHorizontal ? <div className={styles.horizontalMonthHeader}><h3 className={clsx(styles.monthHeader, styles.monthHeaderHorizontal)} data-test-id="range-calendar-month-header-2">{monthFormatter.format(secondMonth.toDate(getLocalTimeZone()))}</h3><Button variant="icon" ariaLabel={t("next_month")} slot="next"><NavArrowRight decorative={true} size="large" color="currentColor" /></Button></div> : <h3 className={styles.monthHeader} data-test-id="range-calendar-month-header-2">{monthFormatter.format(secondMonth.toDate(getLocalTimeZone()))}</h3>}<CalendarGrid offset={{
              months: 1
            }} weekdayStyle={isConnected ? "narrow" : "short"} className={styles["react-aria-CalendarGrid"]}><CalendarGridHeader>{_temp4}</CalendarGridHeader><CalendarGridBody className={styles["react-aria-CalendarGridBody"]}>{date_3 => {
                  const isAnchorDate_0 = renderProps.state.anchorDate ? date_3.compare(renderProps.state.anchorDate) === 0 : false;
                  const withinMinDuration_0 = isDateUnavailable(date_3, minDuration, renderProps.state.anchorDate);
                  const dow_0 = (date_3.toDate(getLocalTimeZone()).getDay() + 6) % 7;
                  return <CalendarCell date={date_3} aria-disabled={isAnchorDate_0 || withinMinDuration_0} className={t13 => {
                    const {
                      isUnavailable: isUnavailable_1
                    } = t13;
                    return clsx({
                      [styles["react-aria-CalendarCell"]]: true,
                      [styles.isRoundedLeft]: dow_0 === 0 || date_3.day === 1,
                      [styles.isRoundedRight]: dow_0 === 6 || date_3.day === date_3.calendar.getDaysInMonth(date_3),
                      [styles.isUnavailable]: isUnavailable_1 || withinMinDuration_0,
                      [styles.isAnchorDate]: isAnchorDate_0
                    });
                  }}>{isConnected ? _temp5 : undefined}</CalendarCell>;
                }}</CalendarGridBody></CalendarGrid></div>}</div>{errorMessage && <Text className={styles["react-aria-text"]} slot="errorMessage">{errorMessage}</Text>}</>;
    };
    $[24] = errorMessage;
    $[25] = fixedDuration;
    $[26] = isConnected;
    $[27] = isHorizontal;
    $[28] = isStartDateUnavailable;
    $[29] = minDuration;
    $[30] = monthFormatter;
    $[31] = monthsToShow;
    $[32] = t;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== isCellUnavailable || $[35] !== props || $[36] !== t10 || $[37] !== t11 || $[38] !== t8) {
    t12 = <AriaRangeCalendar data-test-id="RangeCalendar" visibleDuration={t8} pageBehavior="single" className={t10} minValue={earliestBookingDate} firstDayOfWeek="mon" allowsNonContiguousRanges={true} {...props} isDateUnavailable={isCellUnavailable}>{t11}</AriaRangeCalendar>;
    $[34] = isCellUnavailable;
    $[35] = props;
    $[36] = t10;
    $[37] = t11;
    $[38] = t8;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  return t12;
};
export { RangeCalendar };
function _temp(date, minDuration_0, anchorDate) {
  if (!anchorDate || !minDuration_0) {
    return false;
  }
  const invalidRanges = [[anchorDate, anchorDate.add({
    days: minDuration_0
  })], [anchorDate.subtract({
    days: minDuration_0
  }), anchorDate]];
  const isUnavailable = invalidRanges.some(range => date.compare(range[0]) > 0 && date.compare(range[1]) < 0);
  return isUnavailable;
}
function _temp2(day) {
  return <CalendarHeaderCell className={styles["react-aria-CalendarHeaderCell"]}>{day}</CalendarHeaderCell>;
}
function _temp3(t0) {
  const {
    defaultChildren
  } = t0;
  return <span className={styles.cellInner}>{defaultChildren}</span>;
}
function _temp4(day_0) {
  return <CalendarHeaderCell className={styles["react-aria-CalendarHeaderCell"]}>{day_0}</CalendarHeaderCell>;
}
function _temp5(t0) {
  const {
    defaultChildren: defaultChildren_0
  } = t0;
  return <span className={styles.cellInner}>{defaultChildren_0}</span>;
}